import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

// `PUT /chat/{id}` currently overwrites every column from the request
// body — sending a partial { message } would null out chatGroupId,
// assignToStaffId, parentChatId, etc. So callers must pass the full
// original message object and we spread the new text on top.
//
// Ownership isn't enforced server-side yet either, so the *UI* is the
// real "own messages only" guard; the components only render the edit
// affordance when `createdByStaffId === me.staffMember.id`. Once the
// backend lands a 403 guard we'll catch it here and let the caller
// surface an inline notice.
export const useEditChatMessage = () => {
  const queryClient = useQueryClient();
  const handleNetworkError = useHandleNetworkError("Error editing message");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const invalidateThreads = () => {
    queryClient.invalidateQueries({ queryKey: ["chatDm"] });
    queryClient.invalidateQueries({ queryKey: ["chatGroup"] });
    queryClient.invalidateQueries({ queryKey: ["chats"] });
    queryClient.invalidateQueries({ queryKey: ["chatGroups"] });
  };

  return useMutation({
    mutationFn: async ({ originalMessage, message }) => {
      if (!originalMessage?.id) {
        throw new Error("Cannot edit a message without an id");
      }
      const axiosInstance = createAxiosInstance();
      // Strip the local-only fields so we don't echo `_outgoing` /
      // `_status` back to the server.
      const { _outgoing, _status, ...serverShape } = originalMessage;
      const body = { ...serverShape, message };
      const response = await axiosInstance.put(
        `${tenantVal}/chat/${originalMessage.id}`,
        body
      );

      return response.data;
    },
    onSuccess: () => {
      toast.success("Message updated");
      invalidateThreads();
    },
    onError: (error) => {
      const status = error?.response?.status;
      // 403 (ownership guard) and 404 (message gone) are expected
      // branches — the component renders an inline message or just
      // refreshes the thread. Skip the generic red toast.
      if (status === 403) return;
      if (status === 404) {
        invalidateThreads();
        return;
      }
      handleNetworkError(error);
    },
  });
};
